import { TransactionChunk, id } from '@instantdb/core';
import { BACKEND_BASE_URL, IMAGE_CACHE_NAME } from './constants';
import { getFileExtension, sha256Array } from './utils';
import { db } from './instant';
import { AppSchema } from '@zw-app/shared/instant.schema';
import { CreateMap } from './schema';
import pdfjs from './pdfWorker';

export const newEmptyMap = (): CreateMap => ({
    id: id(),
    name: '',
    bgImage: null,
});

export const bgImageKey = (index: number) => `newMaps[${index.toString()}].bgImage`;
export const mapNameKey = (index: number) => `newMaps[${index.toString()}].name`;

// Convert PDF to JPG if needed
export const convertPdfToJpg = async (file: File, dpi = 150): Promise<File> => {
    if (file.type === 'application/pdf') {
        try {
            console.log('Converting PDF to JPG...');

            // Load the PDF file
            const pdfData = await file.arrayBuffer();
            const pdf = await pdfjs.getDocument({ data: pdfData }).promise;

            // Get the first page
            const page = await pdf.getPage(1);

            // Calculate scale based on desired DPI (72 DPI is the PDF default)
            const scale = dpi / 72;
            const viewport = page.getViewport({ scale });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            if (!context) {
                throw new Error('Failed to get canvas context');
            }

            // Set canvas dimensions to match the page
            canvas.width = viewport.width;
            canvas.height = viewport.height;

            // Render the PDF page to the canvas
            await page.render({
                canvas: canvas,
                canvasContext: context,
                viewport: viewport,
            }).promise;

            // Convert canvas to blob
            const blob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(
                    blob => {
                        if (blob === null) {
                            reject(new Error('Failed to create blob from canvas'));
                        } else {
                            resolve(blob);
                        }
                    },
                    'image/jpeg',
                    0.95
                );
            });

            // Create a new file with the converted image
            const fileName = file.name.replace(/\.pdf$/i, '.jpg');
            return new File([blob], fileName, { type: 'image/jpeg' });
        } catch (error) {
            console.error('Error converting PDF to JPG:', error);
            throw new Error('Failed to convert PDF to JPG');
        }
    }

    // Return the original file if it's not a PDF
    return file;
};

/**
 * Normalize a backend image URL to the current BACKEND_BASE_URL. The DB can
 * contain a mix of prod and dev URLs. We request and cache only one origin so
 * the service worker cache stays consistent.
 */
export function normalizeImageUrl(url: string): string {
    try {
        const parsed = new URL(url);
        if (parsed.pathname.startsWith('/images/')) {
            const base = new URL(BACKEND_BASE_URL);
            return `${base.origin}${parsed.pathname}${parsed.search}`;
        }
    } catch {
        // ignore invalid URLs
    }
    return url;
}

export const getImageUrl = async (file: File): Promise<string> => {
    console.log('file', file);
    // Get the filename
    const content = await file.arrayBuffer();
    const hashHex = await sha256Array(content);

    const fileExtension = getFileExtension(file.name);
    const fileName = `${hashHex}.${fileExtension}`;

    const imgUrl = `${BACKEND_BASE_URL}/images/${fileName}`;
    return imgUrl;
};

// Manually insert image response into service worker cache
// so that we can view the map before uploading while offline.
// Use a plain Request(url) so the cache key matches what the page uses
// for GET (img src / fetch(url)); including token in the key would make
// the entry unreadable by the service worker's CacheFirst for images.
export const cacheImage = async (file: File, url: string, _token: string): Promise<void> => {
    const cache = await window.caches.open(IMAGE_CACHE_NAME);
    const req = new Request(url);
    const res = new Response(file, {
        headers: {
            'Content-Type': file.type,
        },
    });
    await cache.put(req, res);
};

/**
 * Remove an image from the service worker cache. Call this when the app
 * deletes an image (e.g. after a successful DELETE /images/:key) so the
 * cache does not continue to serve the removed image offline.
 */
export const deleteCachedImage = async (url: string): Promise<void> => {
    if (typeof window === 'undefined' || window.caches === undefined) return;
    const cache = await window.caches.open(IMAGE_CACHE_NAME);
    await cache.delete(url);
};

// Upload image to R2
export const uploadImage = async (file: File, url: string, token: string): Promise<void> => {
    const res = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': file.type,
            token,
        },
    });
    if (!res.ok) {
        let bodyText: string | undefined;
        try {
            bodyText = await res.text();
        } catch {
            // ignore
        }
        throw new Error(`uploadImage failed: ${res.status} ${res.statusText}${bodyText ? ` - ${bodyText}` : ''}`);
    }
};

export interface CreateMapsParams {
    newMaps: CreateMap[];
    eventId: string;
    orderOffset?: number;
    otherTxns: TransactionChunk<AppSchema, any>[];
    token: string;
    setIsUploading: (is: boolean) => void;
}

function summarizeCreateMapsParams(params: CreateMapsParams) {
    return {
        eventId: params.eventId,
        newMapsCount: params.newMaps.length,
        newMapsWithBgImageCount: params.newMaps.filter(m => m.bgImage != null).length,
        otherTxnsCount: params.otherTxns.length,
        orderOffset: params.orderOffset ?? 0,
    };
}

async function withTimeout<T>(p: Promise<T>, timeoutMs: number, label: string): Promise<T> {
    let timeoutHandle: number | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutHandle = window.setTimeout(() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)), timeoutMs);
    });
    try {
        return await Promise.race([p, timeoutPromise]);
    } finally {
        if (timeoutHandle !== undefined) window.clearTimeout(timeoutHandle);
    }
}

export async function createMaps(params: CreateMapsParams): Promise<void> {
    const uploadPromises: Promise<void>[] = [];

    const { newMaps, eventId, otherTxns, token, setIsUploading } = params;

    const orderOffset = params.orderOffset || 0;

    // Copy txns into new array
    const txs = [...otherTxns];

    // Show loading spinner
    setIsUploading(true);

    // It's not ideal to await in a loop,
    // but these are not network requests,
    // just async browser operations.
    // So it's probably alright.
    for (let index = 0; index < newMaps.length; index++) {
        let map = newMaps[index];

        if (map.bgImage === null) {
            console.error('Map has no bgImage - skipping', map);
            continue;
        }

        const imgUrl = await getImageUrl(map.bgImage);

        // Before uploading, cache image so that it can be viewed
        // even if we're currently offline.
        await cacheImage(map.bgImage, imgUrl, token);

        // Try to upload image.
        // service worker will retry later
        // if we're currently offline
        const uploadPromise = uploadImage(map.bgImage, imgUrl, token);
        uploadPromises.push(uploadPromise);

        // Append transaction
        txs.push(
            db.tx.maps[map.id]
                .update({
                    name: map.name,
                    bgImageUrl: imgUrl,
                    // keep maps in a consistent order
                    order: index + orderOffset,
                })
                .link({ event: eventId })
        );
    }

    try {
        // Wait for uploads to complete BEFORE updating the database
        // If a fetch hangs (rare but possible), avoid an indefinite "Save" that looks like a no-op.
        await withTimeout(Promise.all(uploadPromises), 30_000, 'createMaps image uploads');

        // Update InstantDB only after all uploads are complete
        await withTimeout(db.transact(txs), 30_000, 'createMaps db.transact');
    } catch (err) {
        console.error('[createMaps] failed', {
            ...summarizeCreateMapsParams(params),
            hasUploads: uploadPromises.length > 0,
            error: err,
        });
        // Propagate so callers can keep edit mode and show feedback.
        throw err;
    } finally {
        setIsUploading(false);
    }
}
